import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { newDesignProjectPath } from '@/lib/designChoice';

export const HIGHLIGHT_PARAM = 'highlight';
export const HIGHLIGHT_TYPE_SESSION = 'session';
export const HIGHLIGHT_TYPE_PROJECT = 'project';

const HIGHLIGHT_DURATION_MS = 4000;
const HIGHLIGHT_BG_ANIMATIONS = new Set(['teleportBgPulseSession', 'teleportBgPulseProject']);
const HIGHLIGHT_CLASS = 'teleport-highlight';
const HIGHLIGHT_CLASS_SESSION = 'teleport-highlight--session';
const HIGHLIGHT_CLASS_PROJECT = 'teleport-highlight--project';

function parseDateYMD(dateStr: string): { year: number; month: number } {
  const [y, m] = (dateStr || '').slice(0, 10).split('-').map(Number);
  return { year: y ?? new Date().getFullYear(), month: (m ?? 1) - 1 };
}

export interface TeleportContextValue {
  goToSession: (session: { id: string; date: string }) => void;
  goToProject: (projectId: string) => void;
  onScheduleMonthChange?: (year: number, month: number) => void;
}

export function useTeleportToTarget(onScheduleMonthChange?: (year: number, month: number) => void): TeleportContextValue {
  const navigate = useNavigate();

  const goToSession = useCallback(
    (session: { id: string; date: string }) => {
      const { year, month } = parseDateYMD(session.date);
      onScheduleMonthChange?.(year, month);
      setTimeout(() => {
        navigate(`/schedule?${HIGHLIGHT_PARAM}=${HIGHLIGHT_TYPE_SESSION}:${session.id}`);
      }, 0);
    },
    [navigate, onScheduleMonthChange]
  );

  // Всегда карточка проекта в новом дизайне, при любом выбранном
  const goToProject = useCallback(
    (projectId: string) => {
      navigate(newDesignProjectPath(projectId));
    },
    [navigate]
  );

  return { goToSession, goToProject, onScheduleMonthChange };
}

/** Хук для страницы: читает ?highlight= из URL, находит элемент, скроллит в центр и проигрывает анимацию подсветки */
export function useHighlightTarget(
  type: 'session' | 'project',
  contentReady: boolean,
  _scrollContainerRef: React.RefObject<HTMLElement | null>
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedRawRef = useRef<string | null>(null);
  const highlightInUrlRef = useRef<string | null>(null);
  highlightInUrlRef.current = searchParams.get(HIGHLIGHT_PARAM);

  useEffect(() => {
    if (!contentReady) return;
    const raw = searchParams.get(HIGHLIGHT_PARAM);
    if (!raw) {
      consumedRawRef.current = null;
      return;
    }
    if (consumedRawRef.current === raw) return;
    const [t, id] = raw.split(':');
    if (t !== type || !id) return;

    const selector = type === 'session'
      ? `[data-session-id="${id}"]`
      : `[data-project-id="${id}"]`;

    const tryFind = (attempt = 0) => {
      if (highlightInUrlRef.current !== raw) return;
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        if (highlightInUrlRef.current !== raw) return;
        consumedRawRef.current = raw;
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete(HIGHLIGHT_PARAM);
          return next;
        }, { replace: true });
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        const typeClass = type === 'session' ? HIGHLIGHT_CLASS_SESSION : HIGHLIGHT_CLASS_PROJECT;
        el.classList.add(HIGHLIGHT_CLASS, typeClass);
        let highlightRemoved = false;
        const removeHighlight = () => {
          if (highlightRemoved) return;
          highlightRemoved = true;
          el.removeEventListener('animationend', onAnimationEnd);
          clearTimeout(fallbackTimer);
          el.classList.remove(HIGHLIGHT_CLASS, typeClass);
        };
        const onAnimationEnd = (e: AnimationEvent) => {
          if (!HIGHLIGHT_BG_ANIMATIONS.has(e.animationName)) return;
          removeHighlight();
        };
        el.addEventListener('animationend', onAnimationEnd);
        const fallbackTimer = setTimeout(removeHighlight, HIGHLIGHT_DURATION_MS + 150);
        return;
      }
      if (attempt < 80) {
        requestAnimationFrame(() => tryFind(attempt + 1));
      }
    };

    requestAnimationFrame(() => tryFind(0));
  }, [contentReady, searchParams, setSearchParams, type]);
}
